import type { QueryClient } from "@tanstack/react-query";

/**
 * Refetch dashboard data that aggregates follow-ups (list widget, analytics
 * drawer, summary cards). Call after lead/deal follow-up create/complete PATCH.
 *
 * THIS IS THE ONLY follow-up refresh path (2026-09-01). The server's
 * `FOLLOWUP_CHANGED` push and the widgets' 60s polling were both removed —
 * they reloaded everyone's dashboard on a timer for work that wasn't theirs,
 * and each reload rebuilt the follow-up bucket index from scratch. So this
 * runs for the ACTOR only, right after their own write, against a server cache
 * their request already deleted (`invalidateUserScopedCachesNow`, awaited
 * before the response) — which is what makes the refetch come back fresh.
 */
export function invalidateFollowUpDashboardQueries(
  queryClient: QueryClient
): void {
  void queryClient.invalidateQueries({ queryKey: ["followups"] });
  void queryClient.invalidateQueries({ queryKey: ["followup-analytics"] });
  void queryClient.invalidateQueries({ queryKey: ["dashboard-basics"] });
  // v2 dashboard follow-ups widget (live data) + the "new leads" tab.
  void queryClient.invalidateQueries({ queryKey: ["v2-followups"] });
  void queryClient.invalidateQueries({ queryKey: ["v2-new-leads"] });
  // The KPI strip's "Active follow-ups" + "Overdue" tiles read the SAME
  // counters as the rail. The socket push used to be what refreshed them;
  // without it they would sit on the actor's own stale numbers after they
  // rescheduled something.
  void queryClient.invalidateQueries({ queryKey: ["v2-kpi-summary"] });
}

export function isFollowUpPatchType(type: string | undefined): boolean {
  // `completeFollowUp` is gone (2026-08-20) — follow-ups are always live and
  // can only be rescheduled, so `updateFollowup` is the only patch that moves
  // a follow-up and therefore the only one that needs these invalidations.
  return type === "updateFollowup";
}
